/*
 * @Copyright 2026. Institute for Future Intelligence, Inc.
 */

// Desktop-only persistence of the user's "mute the undo toast" decision.
// Mobile keeps the toast on every session because mobile has no Ctrl+Z
// equivalent, so the toast button is often the only undo affordance.

import { MOBILE_BREAKPOINT_PX } from './hooks.ts';

export const MUTE_UNDO_MESSAGE_STORAGE_KEY = 'aims-mute-undo-message';

const isMobileViewport = (): boolean => {
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX - 1}px)`).matches;
};

// Read the cached preference. Returns false on mobile (so the undo toast
// always shows up there) and false when localStorage is unavailable
// (private-mode, sandboxed iframe, etc.).
export const readPersistedMuteUndoMessage = (): boolean => {
  if (typeof window === 'undefined' || !window.localStorage) return false;
  if (isMobileViewport()) return false;
  try {
    return window.localStorage.getItem(MUTE_UNDO_MESSAGE_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

// Persist the mute decision on desktop. Silently no-ops on mobile so a
// brief mobile session doesn't permanently silence the toast for that
// browser. Also no-ops if localStorage throws (private mode, quota, etc.)
// since this preference is non-critical.
export const persistMuteUndoMessage = (): void => {
  if (typeof window === 'undefined' || !window.localStorage) return;
  if (isMobileViewport()) return;
  try {
    window.localStorage.setItem(MUTE_UNDO_MESSAGE_STORAGE_KEY, 'true');
  } catch {
    // ignore
  }
};
